import { useSyncExternalStore } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { downloadBlob } from "@/utils/download";

export type FileDownloadState = "downloading" | "done";

// Module-level so the indicator survives collapsing/expanding document rows;
// a full page refresh clears it, which is exactly the intended lifetime.
const statuses = new Map<string, FileDownloadState>();
const listeners = new Set<() => void>();

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function setStatus(fileId: string, state: FileDownloadState | null) {
  if (state === null) statuses.delete(fileId);
  else statuses.set(fileId, state);
  listeners.forEach((l) => l());
}

export function useFileDownloadStatus(fileId: string): FileDownloadState | undefined {
  return useSyncExternalStore(subscribe, () => statuses.get(fileId));
}

export async function downloadFileTracked(
  fileId: string,
  url: string,
  filename: string,
): Promise<void> {
  setStatus(fileId, "downloading");
  try {
    const response = await fetch(url);
    downloadBlob(await response.blob(), filename);
    setStatus(fileId, "done");
  } catch {
    // Fallback hands the download to the browser — completion is unknowable
    // from here, so show no status rather than a false "Pobrano".
    setStatus(fileId, null);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function FileDownloadStatusBadge({ fileId }: { fileId: string }) {
  const state = useFileDownloadStatus(fileId);
  if (!state) return null;
  if (state === "downloading") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 flex-shrink-0">
        <Loader2 size={11} className="animate-spin flex-shrink-0" />
        Rozpoczęto pobieranie…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400 flex-shrink-0">
      <CheckCircle2 size={11} className="flex-shrink-0" />
      Pobrano
    </span>
  );
}
